// src/utils/instructorCopilot.js
//
// Utilities for the instructor copilot quick-input drafting workflow (Pass 30).
// Stateless. No Firestore. No React. No AI calls.
// All functions are pure — safe to call from any context.

export const TONE_OPTIONS = ['warm', 'professional', 'motivational', 'brief'];
export const MAX_NOTES_LENGTH = 500;
export const MAX_FOCUS_TAGS = 5;

/**
 * Returns a safe, bounded QuickInput object from raw instructor input.
 * Caps all fields. Validates tone. Never throws.
 *
 * @param {object} [rawInput]
 * @returns {{ notes: string, targetSkillId: string, focusTags: string[], tone: string, duration: number|null }}
 */
export function normalizeQuickInput(rawInput = {}) {
  const r = rawInput || {};

  const notes = String(r.notes || '').trim().slice(0, MAX_NOTES_LENGTH);
  const targetSkillId = String(r.targetSkillId || '').trim().slice(0, 80);
  const focusTags = (Array.isArray(r.focusTags) ? r.focusTags : [])
    .slice(0, MAX_FOCUS_TAGS)
    .map((t) => String(t || '').trim().slice(0, 40))
    .filter(Boolean);
  const tone = TONE_OPTIONS.includes(r.tone) ? r.tone : 'professional';
  const duration =
    typeof r.duration === 'number' && r.duration > 0
      ? Math.min(Math.max(Math.round(r.duration), 15), 240)
      : null;

  return { notes, targetSkillId, focusTags, tone, duration };
}

/**
 * Builds a safe, readable string that the AI payload builder appends to the
 * task instruction. Each piece of instructor context becomes a phrase.
 *
 * Returns an empty string when all inputs are empty — callers check this
 * before appending, so the base task instruction is unchanged.
 *
 * @param {object} [quickInput]
 * @returns {string}
 */
export function buildInstructorContextClause(quickInput) {
  const q = normalizeQuickInput(quickInput);
  const parts = [];

  if (q.notes) parts.push(`Coach notes: ${q.notes}`);
  if (q.targetSkillId) parts.push(`Target skill: ${q.targetSkillId}`);
  if (q.focusTags.length > 0) parts.push(`Focus areas: ${q.focusTags.join(', ')}`);
  if (q.duration) parts.push(`Planned duration: ${q.duration} min`);
  if (q.tone !== 'professional') parts.push(`Preferred tone: ${q.tone}`);

  return parts.join('. ');
}

/**
 * Returns a compact display string summarising what the instructor provided.
 * Used by AiDraftContextSummary and AiDraftContextChips.
 *
 * @param {object} [quickInput]
 * @returns {string}  e.g. '"late edge change" · Skill: carving · pressure timing · 90 min'
 */
export function buildQuickInputSummaryText(quickInput) {
  const q = normalizeQuickInput(quickInput);
  const parts = [];

  if (q.notes) {
    const truncated = q.notes.length > 60 ? `${q.notes.slice(0, 57)}…` : q.notes;
    parts.push(`"${truncated}"`);
  }
  if (q.targetSkillId) parts.push(`Skill: ${q.targetSkillId}`);
  if (q.focusTags.length > 0) parts.push(q.focusTags.join(', '));
  if (q.duration) parts.push(`${q.duration} min`);

  return parts.join(' · ') || 'No context added';
}

/**
 * Returns true when no meaningful instructor input has been provided.
 * Used to decide whether to show the "signal-only" warning.
 *
 * @param {object} [quickInput]
 * @returns {boolean}
 */
export function isQuickInputEmpty(quickInput) {
  const q = normalizeQuickInput(quickInput);
  return !q.notes && !q.targetSkillId && q.focusTags.length === 0 && !q.duration;
}

/**
 * Returns a default QuickInput object with all fields reset.
 *
 * @returns {{ notes: string, targetSkillId: string, focusTags: string[], tone: string, duration: number|null }}
 */
export function emptyQuickInput() {
  return { notes: '', targetSkillId: '', focusTags: [], tone: 'professional', duration: null };
}
